// Business logo upload (backlog PRINT-2). One logo per account, stored
// alongside doc-photos in the same private account-files bucket — see
// migration 0046's own comment reserving {accountId}/logo/... for this
// exact feature, and migration 0055 for the accounts.logo_path column and
// the bucket's widened allowed_mime_types (JPEG+PNG, was JPEG-only).
//
// Unlike doc-photos (many, JPEG, no per-account overwrite), a logo is a
// single overwritable file at a fixed path, PNG-only (transparency, per the
// PRINT-2 design brief — JPEGs can't carry an alpha channel), and capped at
// 2MB client-side before upload even starts — a UI-level constant with no
// backing Postgres/Storage enforcement, same pattern as MAX_PHOTOS.
import { supabase } from "@/lib/supabase";
import { requireAccountId } from "@/lib/profile";

const BUCKET = "account-files";
const MAX_DIMENSION = 800; // long-edge cap — a logo never needs photo-scale resolution
const MAX_LOGO_BYTES = 2 * 1024 * 1024; // 2MB, enforced before upload starts (PRINT-2 design brief)
const SIGNED_URL_TTL_SECONDS = 60 * 60; // 1 hour — regenerated fresh at every render, never persisted, same as resolvePhotoUrl

/** The uploaded file isn't a PNG — thrown before any decode is attempted. */
export class NotPngError extends Error {}
/** The uploaded file is over MAX_LOGO_BYTES — thrown before any decode is attempted. */
export class LogoTooLargeError extends Error {}
/** The browser couldn't decode the file as an image at all (same DOCS-37 case photoUpload.ts already handles). */
export class UnsupportedLogoImageError extends Error {}

function logoPath(accountId: string): string {
  return `${accountId}/logo/logo.png`;
}

/** Resizes to a max long-edge of MAX_DIMENSION and re-encodes as PNG (preserves transparency). Never upscales a smaller source image. */
async function compressLogo(file: File): Promise<Blob> {
  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(file);
  } catch {
    throw new UnsupportedLogoImageError(`Could not decode "${file.name}" as an image.`);
  }
  const scale = Math.min(1, MAX_DIMENSION / Math.max(bitmap.width, bitmap.height));
  const width = Math.round(bitmap.width * scale);
  const height = Math.round(bitmap.height * scale);

  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Could not get a 2D canvas context to compress this logo.");
  ctx.drawImage(bitmap, 0, 0, width, height);
  bitmap.close();

  return await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error("Could not encode the logo."))), "image/png");
  });
}

/**
 * Uploads (or replaces) the account's logo, returning the bucket-relative
 * path to store on Account.logo_path. Fixed filename + upsert: true — a
 * logo is a single overwritable slot, not a growing list like doc-photos.
 */
export async function uploadLogo(file: File): Promise<string> {
  if (file.type !== "image/png") {
    throw new NotPngError(`"${file.name}" isn't a PNG.`);
  }
  if (file.size > MAX_LOGO_BYTES) {
    throw new LogoTooLargeError(`"${file.name}" is over 2 MB.`);
  }
  const [accountId, compressed] = await Promise.all([requireAccountId(), compressLogo(file)]);
  const path = logoPath(accountId);

  const { error } = await supabase.storage.from(BUCKET).upload(path, compressed, {
    contentType: "image/png",
    upsert: true,
  });
  if (error) throw error;

  return path;
}

/** Removes the account's logo from Storage. Caller is responsible for nulling Account.logo_path afterward. */
export async function removeLogo(path: string): Promise<void> {
  const { error } = await supabase.storage.from(BUCKET).remove([path]);
  if (error) throw error;
}

/**
 * Resolves a stored logo path to a real, viewable URL. The bucket is
 * private, so a plain public URL never works, and a signed URL generated
 * once would go stale if viewed weeks later — always regenerate at render
 * time instead of persisting the result (same pattern as resolvePhotoUrl).
 */
export async function resolveLogoUrl(path: string): Promise<string> {
  const { data, error } = await supabase.storage.from(BUCKET).createSignedUrl(path, SIGNED_URL_TTL_SECONDS);
  if (error) throw error;
  return data.signedUrl;
}
